import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
} from '@mui/material';
import { Category } from '../types';
import {
  NewsflowDisplayDuring,
  NewsflowPublishMode,
  NewsflowRotation,
  NewsflowStationSettings,
  DEFAULT_NEWSFLOW_DURATION_SECONDS,
  defaultNewsflowStationSettings,
  loadNewsflowStationSettings,
  saveNewsflowStationSettings,
} from '../utils/newsflowStationSettings';
import { figma } from '../theme/figmaTokens';

interface NewsflowStationSettingsPanelProps {
  stationId: string;
  categories: Category[];
  onSaved?: (settings: NewsflowStationSettings) => void;
}

const KEEP_ALIVE_OPTIONS = [30, 60, 90, 120, 180];
const DURATION_OPTIONS = [15, 30, 60, 90, 120];

const NewsflowStationSettingsPanel: React.FC<NewsflowStationSettingsPanelProps> = ({
  stationId,
  categories,
  onSaved,
}) => {
  const [settings, setSettings] = useState<NewsflowStationSettings>(() =>
    loadNewsflowStationSettings(stationId, categories)
  );
  const [savedMsg, setSavedMsg] = useState('');

  useEffect(() => {
    setSettings(loadNewsflowStationSettings(stationId, categories));
    setSavedMsg('');
  }, [stationId, categories]);

  const update = <K extends keyof NewsflowStationSettings>(key: K, value: NewsflowStationSettings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setSavedMsg('');
  };

  const handleCategory = (categoryId: string) => {
    const cat = categories.find((c) => c.id === categoryId) || categories.find((c) => c.name === categoryId);
    setSettings((prev) => ({
      ...prev,
      categoryId,
      categoryName: cat?.name ?? categoryId,
    }));
    setSavedMsg('');
  };

  const handleSave = () => {
    const toSave: NewsflowStationSettings = {
      ...settings,
      durationSeconds: settings.durationSeconds || DEFAULT_NEWSFLOW_DURATION_SECONDS,
      publishMode: settings.publishMode === 'automatic' ? 'automatic' : 'manual',
    };
    saveNewsflowStationSettings(stationId, toSave);
    setSavedMsg('Newsflow settings saved for this station.');
    onSaved?.(toSave);
  };

  const handleReset = () => {
    const defaults = defaultNewsflowStationSettings(categories);
    setSettings(defaults);
    setSavedMsg('');
  };

  const takeover = settings.rotation === 'takeover';

  return (
    <Box
      sx={{
        p: 2.5,
        backgroundColor: figma.cardBg,
        border: `1px solid ${figma.border}`,
        borderRadius: 1,
      }}
    >
      <Typography variant="h6" sx={{ color: figma.text, fontWeight: 600, mb: 0.5 }}>
        Quu Card / News Flow
      </Typography>
      <Typography variant="body2" sx={{ color: figma.textMuted, mb: 2.5, maxWidth: 640 }}>
        Configure how cards sent from the Quu Dashboard (&quot;Send to Quu&quot;) become HotQuus for this
        station.
      </Typography>

      {savedMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSavedMsg('')}>
          {savedMsg}
        </Alert>
      )}

      <Typography variant="subtitle2" sx={{ color: figma.text, mb: 1 }}>
        Publish mode
      </Typography>
      <FormControl component="fieldset" sx={{ mb: 2.5 }}>
        <RadioGroup
          value={settings.publishMode}
          onChange={(e) => update('publishMode', e.target.value as NewsflowPublishMode)}
        >
          <FormControlLabel
            value="manual"
            control={<Radio size="small" sx={{ color: figma.textMuted }} />}
            label={
              <Box>
                <Typography variant="body2" sx={{ color: figma.text }}>
                  Manual
                </Typography>
                <Typography variant="caption" sx={{ color: figma.textMuted, display: 'block' }}>
                  Cards are queued as Ready HotQuus with the settings below. Publish them on air from
                  HotQuus when you choose.
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', mb: 1.5 }}
          />
          <FormControlLabel
            value="automatic"
            control={<Radio size="small" sx={{ color: figma.textMuted }} />}
            label={
              <Box>
                <Typography variant="body2" sx={{ color: figma.text }}>
                  Automatic
                </Typography>
                <Typography variant="caption" sx={{ color: figma.textMuted, display: 'block' }}>
                  When a Quu Dashboard card is received for this station, create and publish the HotQuu
                  on air immediately using the settings below.
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start' }}
          />
        </RadioGroup>
      </FormControl>

      <Typography variant="subtitle2" sx={{ color: figma.text, mb: 1.5 }}>
        Default HotQuu settings
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 420 }}>
        <FormControl size="small" fullWidth>
          <InputLabel sx={{ color: figma.textMuted }}>HotQuu category destination</InputLabel>
          <Select
            label="HotQuu category destination"
            value={settings.categoryId}
            onChange={(e) => handleCategory(e.target.value)}
            sx={{ color: figma.text }}
          >
            {categories.length === 0 ? (
              <MenuItem value="Misc">Misc</MenuItem>
            ) : (
              categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth>
          <InputLabel sx={{ color: figma.textMuted }}>Rotation</InputLabel>
          <Select
            label="Rotation"
            value={settings.rotation}
            onChange={(e) => update('rotation', e.target.value as NewsflowRotation)}
            sx={{ color: figma.text }}
          >
            <MenuItem value="takeover">Takeover</MenuItem>
            <MenuItem value="override">Override</MenuItem>
            <MenuItem value="rotate_with">Rotate</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth>
          <InputLabel sx={{ color: figma.textMuted }}>Keep Alive</InputLabel>
          <Select
            label="Keep Alive"
            value={settings.keepAliveMinutes}
            onChange={(e) => update('keepAliveMinutes', Number(e.target.value))}
            sx={{ color: figma.text }}
          >
            {KEEP_ALIVE_OPTIONS.map((m) => (
              <MenuItem key={m} value={m}>
                {m} minutes
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth disabled={takeover}>
          <InputLabel sx={{ color: figma.textMuted }}>Duration in Rotation</InputLabel>
          <Select
            label="Duration in Rotation"
            value={settings.durationSeconds}
            onChange={(e) => update('durationSeconds', Number(e.target.value))}
            sx={{ color: figma.text }}
          >
            {DURATION_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {s}s
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth disabled={takeover}>
          <InputLabel sx={{ color: figma.textMuted }}>Display During</InputLabel>
          <Select
            label="Display During"
            value={settings.displayDuring}
            onChange={(e) => update('displayDuring', e.target.value as NewsflowDisplayDuring)}
            sx={{ color: figma.text }}
          >
            <MenuItem value="other">Other</MenuItem>
            <MenuItem value="ads">Ads</MenuItem>
            <MenuItem value="other_ads">Other + Ads</MenuItem>
          </Select>
        </FormControl>

        {takeover && (
          <Typography variant="caption" sx={{ color: figma.textMuted }}>
            Duration and Display During apply to Override and Rotate. Takeover uses all display-during
            options.
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, mt: 3, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{
            textTransform: 'none',
            color: '#ffffff',
            backgroundColor: '#4DAADB',
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#409dcf', boxShadow: 'none' },
          }}
        >
          Save settings
        </Button>
        <Button
          onClick={handleReset}
          sx={{ textTransform: 'none', color: '#999999' }}
        >
          Reset to defaults
        </Button>
      </Box>
    </Box>
  );
};

export default NewsflowStationSettingsPanel;
